<script setup lang="ts">
import dayjs from 'dayjs'
import { formatMoney } from '~/utils/currencyHelpers'
import { convertLocaleIntoBCP47 } from '~/utils/localeHelpers'
import IconStarFull from '~/assets/icons/review/icon-star-full.svg?component'
import { useLocalePath } from '#imports'

const localePath = useLocalePath()
const { locale, t } = useI18n()

// Helper function to format rating with proper locale-specific decimal separator
const formatRating = (rating: string | number, localeValue: string): string => {
  const numericRating = typeof rating === 'string' ? parseFloat(rating) : rating
  if (!numericRating || isNaN(numericRating)) return '5'

  return new Intl.NumberFormat(convertLocaleIntoBCP47(localeValue), {
    maximumSignificantDigits: 2,
    minimumSignificantDigits: 2,
  }).format(numericRating)
}

const props = withDefaults(
  defineProps<{
    reviews?: any
    title?: string
  }>(),
  {
    reviews: null,
    title: '',
  }
)

const isSidebarOpen = ref(false)
const selectedReviewId = ref<string | undefined>(undefined)

const openSidebar = () => {
  isSidebarOpen.value = true
}

const closeSidebar = () => {
  isSidebarOpen.value = false
  selectedReviewId.value = undefined
}

const onReviewClick = (reviewId: string) => {
  selectedReviewId.value = reviewId
  isSidebarOpen.value = true
}

// Calculated properties for sidebar
const reviewCount = computed(() => {
  const reviewsArray = Array.isArray(props.reviews) ? props.reviews : []
  return reviewsArray.length
})

const averageRating = computed(() => {
  const reviewsArray = Array.isArray(props.reviews) ? props.reviews : []
  if (reviewsArray.length === 0) return formatRating('5', locale.value)

  const sum = reviewsArray.reduce(
    (acc: number, review: any) =>
      acc + parseFloat(review?.event_avg_review || '5'),
    0
  )
  const average = sum / reviewsArray.length
  return formatRating(average, locale.value)
})

const getTransformedReviews = computed(() => {
  if (Array.isArray(props.reviews) && props.reviews.length) {
    return props.reviews.map((review: any) => ({
      ...review,
      created_at: review.review_date,
      id: review.review_id,
      rating: parseFloat(review.event_avg_review),
      customer: review.reviewee_name,
      review: review.review_text,
      media: {
        data: review.reviewMedia?.data || [],
      },
      // Keep the original event media for the main image
      eventMedia: review.media,
    }))
  }

  return []
})

const getReviewDate = (dateString: string) => {
  if (!dateString) return ''

  return dayjs(dateString).format('DD.MM.YYYY')
}
</script>

<template>
  <div>
    <div v-if="getTransformedReviews.length" class="transactional-reviews">
      <h2 class="title">{{ t('review.transactional.title') }} {{ title }}</h2>
      <div
        class="list flex flex-col md:flex-row gap-4 justify-center items-center md:items-stretch"
      >
        <div
          v-for="review in getTransformedReviews.slice(0, 3)"
          :key="review?.id || Math.random()"
          class="item cursor-pointer"
        >
          <div class="item-event">
            <a
              :href="localePath(`/e/${review.event_permalink}/`)"
              target="_blank"
            >
              <NuxtImg
                v-if="review.eventMedia?.data?.thumb"
                :src="review.eventMedia?.data?.thumb"
                :alt="review.eventMedia?.data?.alt || ''"
                loading="lazy"
                class="item-event__image"
              />
              <div
                v-else
                class="item-event__image bg-gray-200 flex items-center justify-center"
              ></div>
            </a>

            <div class="item-event__content" @click="onReviewClick(review?.id)">
              <div v-if="review?.event_price" class="item-event__price">
                {{
                  formatMoney(parseFloat(review.event_price), locale, 'EUR', 2)
                }}
              </div>
              <div class="item-event__rating">
                <div class="flex items-center gap-1">
                  <IconStarFull class="w-4 h-4" />
                  <span
                    >{{ formatRating(review?.event_avg_review || '5', locale) }}
                    –
                    {{
                      t(
                        'review.reviewAmountLabel',
                        Number(review?.event_total_reviews || 1)
                      )
                    }}</span
                  >
                </div>
              </div>
              <div class="item-event__date">
                {{ review?.reviewee_name }},
                {{ getReviewDate(review?.review_date) }}
              </div>
            </div>
          </div>

          <div class="item-content flex-1">
            <div class="text-base font-medium mb-1">
              {{ review?.event_title }}
            </div>
            <div class="text-sm text-stone-500">
              <ReadMore :max-chars="230" :content="review?.review_text" />
            </div>
          </div>

          <a
            :href="localePath(`/e/${review.event_permalink}/`)"
            target="_blank"
            class="event-link"
          >
            {{ t('review.transactional.openEvent') }}
          </a>

          <div
            v-if="
              review?.media?.data &&
              Array.isArray(review.media.data) &&
              review.media.data.length > 0
            "
            class="item-gallery"
            @click="onReviewClick(review?.id)"
          >
            <NuxtImg
              v-for="(media, index) in review.media.data.slice(0, 4)"
              :key="index"
              :src="media?.thumb"
              :alt="media?.alt || ''"
              loading="lazy"
            />
          </div>
        </div>
      </div>

      <div
        v-if="Array.isArray(reviews) && reviews.length > 3"
        class="justify-center flex mt-6"
      >
        <Button
          class="font-semibold max-w-[368px] w-full py-3 md:py-2 text-sm md:text-base"
          @click="openSidebar"
        >
          {{ t('review.seeAll') }} ({{ reviewCount }})
        </Button>
      </div>
    </div>

    <ReviewSidebar
      :is-open="isSidebarOpen"
      :reviews="getTransformedReviews as any"
      :average-rating="averageRating"
      :review-count="reviewCount"
      :selected-review-id="selectedReviewId"
      :is-back-button-hidden="true"
      @close="closeSidebar"
      @update:selected-review-id="selectedReviewId = $event"
    />
  </div>
</template>

<style scoped lang="postcss">
.transactional-reviews {
  @apply relative mt-10 py-10 z-1;
  background: rgba(249, 247, 245, 0.46);

  &::before {
    content: '';
    @apply hidden md:block;
    @apply absolute top-0 bottom-0 left-[-9999px] right-[-9999px] z-[-1];
    background: rgba(249, 247, 245, 1);
  }

  .title {
    @apply text-h6 mb-4 text-center font-semibold;
  }
}

.list {
  @media (min-width: 768px) {
    align-items: stretch !important;
  }
}

.item {
  @apply border-stone-200 border rounded-lg p-2.5 w-full ease-in-out duration-300 bg-white flex flex-col max-w-[406px];
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

  @media (min-width: 768px) {
    flex: 1 1 0%;
    display: flex;
    flex-direction: column;
  }
}

.item-event {
  @apply relative flex items-start mb-3;

  &__image {
    @apply w-[63px] h-[63px] rounded-lg object-cover overflow-hidden mr-2;
  }
}

.item-gallery {
  @apply grid grid-cols-4 gap-2 mt-3;

  img {
    @apply h-20 w-20 rounded-xl object-cover cursor-pointer;
  }
}

.item-event {
  &__price {
    @apply text-base text-gray-900 mb-1;
  }

  &__rating {
    @apply flex items-center gap-1 mb-1 text-xs text-gray-500;
  }

  &__date {
    @apply text-xs text-gray-500;
  }
}

.item-content {
  @apply mb-4;
}

.event-link {
  @apply inline-block mt-auto underline hover:no-underline;

  font-weight: 600;
  font-size: 16px;
  letter-spacing: 0.01em;
  color: #032e32;
}
</style>
